import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Check, MessageSquare, Pencil, Settings2, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import FactionIcon from '@/components/matches/FactionIcon'
import MatchStrategyCard from '@/components/matches/MatchStrategyCard'
import {
  PositionBody, SignUpSection, SquadDisplaySection,
  type RhClass, type SquadData,
} from '@/components/events/EventDetailPanel'
import SignUpBoard from '@/components/matches/SignUpBoard'
import SignUpReminder from '@/components/matches/SignUpReminder'
import type { SignUp } from '@/components/events/types'
import { useAuth } from '@/contexts/AuthContext'
import { useLanguage } from '@/contexts/LanguageContext'
import { factionOf } from '@/lib/factions'
import { fetchMaps, type MapInfo } from '@/lib/maps'
import { clock, dayParts, relativeTo, useNow } from '@/lib/matchTime'
import { roleOf, useRosterCatalog } from '@/lib/rosterCatalog'
import { stepStates, type Match, type MatchProgress, type StepKey, type StepState } from './types'

/** Section heading from the dashboard theme: display face, crimson hairline. */
function SectionTitle({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <h3 className="flex items-center gap-2.5 font-display text-[11px] font-bold uppercase tracking-[0.18em] text-foreground/70">
        <span className="h-px w-6 shrink-0 bg-primary" />
        {children}
      </h3>
      {aside}
    </div>
  )
}

const card = 'rounded-sm border border-white/10 bg-white/[0.035] p-4'

/**
 * A dashboard match: the header, then its three steps — sign-up, position,
 * strategy — as one track the viewer moves along.
 *
 * The track is the page. Each step tells the viewer where *they* stand in it,
 * and the one that waits on them is marked and opened first, so arriving from a
 * "confirm your position" DM lands on the confirm buttons, not on a roster to
 * scroll through.
 */
export default function MatchDetail({
  match, canEdit, canBuildRoster, onEdit, onRemove, onConfigureRoster, onRefresh, onSignUpChange,
}: {
  match: Match
  // Operator: send reminders, publish the strategy, send the roster.
  canEdit: boolean
  // Strategy team (operators + strategist): build/edit the roster. Falls back
  // to canEdit when not given.
  canBuildRoster?: boolean
  onEdit?: () => void
  onRemove?: () => void
  onConfigureRoster?: () => void
  onRefresh: () => void
  onSignUpChange: (su: SignUp | null) => void
}) {
  const canRoster = canBuildRoster ?? canEdit
  const { user } = useAuth()
  const { T } = useLanguage()
  const now = useNow()
  const catalog = useRosterCatalog()
  const ev = match.event
  const eventId = match.event_id

  const [signUps, setSignUps] = useState<SignUp[]>(ev.sign_ups as SignUp[])
  useEffect(() => { setSignUps(ev.sign_ups as SignUp[]) }, [ev.sign_ups])

  const [classes, setClasses] = useState<RhClass[]>([])
  const [classesLoading, setClassesLoading] = useState(true)
  useEffect(() => {
    setClassesLoading(true)
    fetch(`/api/matches/${eventId}/classes`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : { classes: [] }))
      .then(d => setClasses(d.classes ?? []))
      .catch(() => setClasses([]))
      .finally(() => setClassesLoading(false))
  }, [eventId])

  // The published roster and its confirmations, kept fresh while the page is
  // open: people confirm from Discord too.
  const [squads, setSquads] = useState<SquadData[] | null>(null)
  const [confirmations, setConfirmations] = useState<Record<string, string>>({})
  const [bench, setBench] = useState<string[]>([])
  const loadRoster = useCallback(() => {
    fetch(`/api/matches/${eventId}/discord-post`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if (!d.message_id) { setSquads(null); return }
        setSquads(d.squads ?? [])
        setBench((d.unassigned ?? []).map((p: { userId: string }) => p.userId))
        fetch(`/api/matches/${eventId}/confirmations`, { credentials: 'include' })
          .then(r => r.json())
          .then(c => setConfirmations(c ?? {}))
          .catch(() => {})
      })
      .catch(() => {})
  }, [eventId])
  useEffect(() => {
    loadRoster()
    const id = setInterval(loadRoster, 10_000)
    return () => clearInterval(id)
  }, [loadRoster])

  const [maps, setMaps] = useState<MapInfo[]>([])
  useEffect(() => { fetchMaps().then(setMaps).catch(() => {}) }, [])
  const mapInfo = maps.find(m => m.label === ev.map_name || m.name === ev.map_name)

  // Live roster state beats the list's snapshot once it has loaded.
  const progress: MatchProgress | undefined = useMemo(() => {
    if (squads === null) return match.progress
    let position: MatchProgress['position'] = null
    for (const sq of squads) {
      const slot = sq.slots.find(sl => sl.userId === user?.id)
      if (slot) { position = { squad: sq.name, role: slot.role, status: 'pending' }; break }
    }
    if (!position && user && bench.includes(user.id)) position = { squad: '', role: 'substitute', status: 'pending' }
    if (position && user) position.status = (confirmations[user.id] as 'yes' | 'no' | undefined) ?? 'pending'
    return { roster_posted: squads.length > 0, position, strategy: match.progress?.strategy ?? null }
  }, [squads, bench, confirmations, user, match.progress])

  const liveMatch = { ...match, event: { ...ev, sign_ups: signUps }, progress }
  const states = stepStates(liveMatch, user?.id, now)
  const mySignUp = user ? signUps.find(s => s.user_id === user.id) : undefined

  // Open on the step that waits on the viewer; otherwise the furthest one reached.
  const initialStep: StepKey = states.position === 'action' ? 'position'
    : states.signup === 'action' ? 'signup'
    : states.strategy === 'done' ? 'strategy'
    : states.position === 'done' ? 'position'
    : 'signup'
  const [step, setStep] = useState<StepKey>(initialStep)
  // A new match resets the step; a roster arriving later does not yank the view.
  useEffect(() => { setStep(initialStep) }, [eventId]) // eslint-disable-line react-hooks/exhaustive-deps

  const [confirmRemove, setConfirmRemove] = useState(false)

  const handleLocalSignUp = (su: SignUp | null) => {
    setSignUps(prev => {
      const without = prev.filter(s => s.user_id !== user?.id)
      return su ? [...without, su] : without
    })
    onSignUpChange(su)
  }

  const playing = signUps.filter(s => s.is_active).length
  const benchCount = signUps.filter(s => s.is_bench).length

  // ── Step summaries, in the viewer's terms ──
  const roleName = (key: string) => roleOf(catalog, key)?.name ?? key
  const summary: Record<StepKey, string> = {
    signup: mySignUp
      ? T.matches.stepSignupDone.replace('{class}', mySignUp.class_name)
      : states.signup === 'action' ? T.matches.stepSignupTodo : T.matches.stepSignupNone,
    position: !progress?.roster_posted ? T.matches.stepPositionWaiting
      : !progress.position ? T.matches.stepPositionOut
      : progress.position.role === 'substitute' ? T.matches.stepPositionBench
      : `${progress.position.squad} · ${roleName(progress.position.role)}`,
    strategy: progress?.strategy ? progress.strategy.title : T.matches.stepStrategyWaiting,
  }
  const steps: { key: StepKey; label: string }[] = [
    { key: 'signup', label: T.matches.stepSignup },
    { key: 'position', label: T.matches.stepPosition },
    { key: 'strategy', label: T.matches.stepStrategy },
  ]

  const start = ev.start_time
  const day = start ? dayParts(start, T) : null

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-6xl space-y-4 p-4 md:p-6">

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <section className="overflow-hidden rounded-md border border-white/10 bg-black/25 backdrop-blur-sm">
          <div className="flex flex-col gap-4 p-4 md:p-5 lg:flex-row">
            <div className="min-w-0 flex-1 space-y-3">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                {day && start && (
                  <>
                    <span className="text-foreground/80">{day.weekday} {day.day} {day.month}</span>
                    <span className={start > now ? 'text-foreground/60' : 'text-muted-foreground/60'}>
                      {relativeTo(start, now, T)}
                    </span>
                  </>
                )}
                {ev.map_name && <span className="lg:hidden">{ev.map_name}</span>}
                {ev.staff_only && (
                  <span className="rounded-sm border border-amber-400/30 px-1.5 py-px text-[10px] text-amber-400/80">
                    {T.matches.staffOnly}
                  </span>
                )}
              </div>

              {/* Not upper-cased: the title is whatever was typed, and the page
                  is lang="tr", which would turn an English "i" into "İ". */}
              <h2 className="font-display text-xl font-bold leading-tight tracking-[0.04em] md:text-2xl">
                {ev.title}
              </h2>

              {/* The sides: the one place on the page colour carries meaning. */}
              {ev.teams && ev.teams.length > 0 ? (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  {ev.teams.map((t, i) => {
                    const f = factionOf(t.faction)
                    return (
                      <span key={i} className="flex items-center gap-2">
                        {i > 0 && <span className="font-display text-[11px] text-muted-foreground/40">vs</span>}
                        <span
                          className="flex items-center gap-2 rounded-sm border bg-black/30 py-1 pl-1.5 pr-2.5"
                          style={{ borderColor: f ? `${f.color}66` : 'rgba(255,255,255,0.1)' }}
                        >
                          <FactionIcon faction={t.faction} className="h-5 w-5" />
                          <span className="text-sm font-semibold">{t.name}</span>
                          {/* Lifted toward white: the raw faction blue is unreadable on the dark band. */}
                          {f && <span className="text-[11px]" style={{ color: `color-mix(in srgb, ${f.color} 65%, white)` }}>{f.label}</span>}
                        </span>
                      </span>
                    )
                  })}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {T.matches.formTeams}: <span className="italic text-muted-foreground/60">{T.matches.notSetYet}</span>
                </p>
              )}

              {/* The two times are the thing people come back to check. */}
              {start && (
                <dl className="flex flex-wrap gap-x-8 gap-y-2">
                  <div>
                    <dt className="text-[11px] text-muted-foreground">{T.matches.briefing}</dt>
                    <dd className="font-display text-xl tabular-nums">
                      {ev.briefing_time ? clock(ev.briefing_time)
                        : <span className="text-sm italic text-muted-foreground/60">{T.matches.notSetYet}</span>}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] text-muted-foreground">{T.matches.kickoff}</dt>
                    <dd className="font-display text-xl tabular-nums">{clock(start)}</dd>
                  </div>
                </dl>
              )}

              {ev.description && (
                <p className="max-w-prose whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">{ev.description}</p>
              )}

              <div className="flex flex-wrap items-center gap-2">
                {ev.discord_url && (
                  <a href={ev.discord_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex h-8 items-center gap-1.5 rounded-sm border border-white/10 bg-white/5 px-3 text-xs text-foreground/80 transition-colors hover:border-white/20 hover:text-foreground">
                    <MessageSquare className="h-3.5 w-3.5" />{T.matches.openChannel}
                  </a>
                )}
                {onEdit && (
                  <Button size="sm" variant="ghost" className="h-8 gap-1.5 text-xs text-muted-foreground" onClick={onEdit}>
                    <Pencil className="h-3.5 w-3.5" />{T.matches.editMatch}
                  </Button>
                )}
                {onRemove && (confirmRemove ? (
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] text-muted-foreground">{T.matches.deleteConfirm}</span>
                    <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => { setConfirmRemove(false); onRemove() }}>
                      {T.eventDetail.confirmAction}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setConfirmRemove(false)}>
                      {T.eventDetail.cancelAction}
                    </Button>
                  </span>
                ) : (
                  <Button size="sm" variant="ghost" className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-destructive"
                    onClick={() => setConfirmRemove(true)}>
                    <Trash2 className="h-3.5 w-3.5" />{T.eventDetail.removeMatchTitle}
                  </Button>
                ))}
              </div>
            </div>

            {/* The game's own artwork carries the map name — nothing is printed over it. */}
            {/* On a phone the artwork would push the steps below the fold; the
                map's name sits in the line above the title instead. */}
            <figure className="hidden shrink-0 lg:block lg:w-72">
              {mapInfo?.hero ? (
                <img src={mapInfo.hero} alt={mapInfo.label}
                  className="aspect-video w-full rounded-sm border border-white/10 object-cover" />
              ) : (
                <div className="flex aspect-video w-full items-center justify-center rounded-sm border border-dashed border-white/10 text-xs text-muted-foreground">
                  {ev.map_name || <span className="italic">{T.matches.formMap}: {T.matches.notSetYet}</span>}
                </div>
              )}
            </figure>
          </div>
        </section>

        {/* ── The three steps ────────────────────────────────────────────── */}
        <ol className="grid grid-cols-3 overflow-hidden rounded-md border border-white/10 bg-black/25 backdrop-blur-sm" role="tablist">
          {steps.map((s, i) => {
            const state: StepState = states[s.key]
            const active = step === s.key
            return (
              <li key={s.key} className={i > 0 ? 'border-l border-white/10' : ''}>
                <button
                  role="tab"
                  aria-selected={active}
                  onClick={() => setStep(s.key)}
                  className={`relative flex h-full w-full items-start gap-3 px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/60 sm:px-4 ${
                    active ? 'bg-white/[0.06]' : 'hover:bg-white/[0.03]'
                  }`}
                >
                  {active && <span className="absolute inset-x-0 top-0 h-0.5 bg-primary" />}
                  <span className={`font-display text-2xl font-bold leading-none tabular-nums sm:text-3xl ${
                    active ? 'text-foreground' : 'text-foreground/25'
                  }`}>
                    {i + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate font-display text-[11px] font-bold uppercase tracking-[0.08em] sm:text-xs sm:tracking-[0.14em]">{s.label}</span>
                      {state === 'done' && <Check className="h-3.5 w-3.5 text-foreground/50" />}
                    </span>
                    <span className="mt-1 hidden truncate text-xs text-muted-foreground sm:block">{summary[s.key]}</span>
                    {state === 'action' && (
                      <span className="mt-1 flex items-center gap-1.5 text-[11px] font-semibold text-primary">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary motion-safe:animate-pulse" />
                        {T.matches.yourTurn}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            )
          })}
        </ol>

        {/* ── The open step ──────────────────────────────────────────────── */}
        <section key={step} role="tabpanel"
          className="rounded-md border border-white/10 bg-black/25 p-3 backdrop-blur-sm motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-1 motion-safe:duration-200 md:p-5">

          {step === 'signup' && (
            <div className="grid gap-4 xl:grid-cols-[16rem_minmax(0,1fr)]">
              <div>
                <SectionTitle>{T.matches.yourSignUp}</SectionTitle>
                <div className={card}>
                  <SignUpSection
                    eventId={eventId}
                    signupBase="matches"
                    classes={classes}
                    classesLoading={classesLoading}
                    currentSignUp={mySignUp}
                    onLocalSignUpChange={handleLocalSignUp}
                    onRefresh={onRefresh}
                  />
                </div>
              </div>
              <div className="min-w-0">
                <SectionTitle aside={
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {T.matches.playingCount.replace('{n}', String(playing))}
                    {benchCount > 0 && ` · ${T.matches.benchCount.replace('{n}', String(benchCount))}`}
                  </span>
                }>
                  {T.matches.signUpsTitle}
                </SectionTitle>
                {classesLoading
                  ? <div className="h-40 animate-pulse rounded-sm bg-white/5" />
                  : <SignUpBoard signUps={signUps} classes={classes} />}
                {!ev.staff_only && (!ev.start_time || ev.start_time > now) && (
                  <SignUpReminder
                    eventId={eventId}
                    signUpKey={signUps.map(s => `${s.user_id}:${s.class_name}`).join()}
                    canSend={canEdit && !progress?.roster_posted}
                  />
                )}
              </div>
            </div>
          )}

          {step === 'position' && (squads && squads.length > 0 ? (
            <div className="grid gap-5 lg:grid-cols-[20rem_minmax(0,1fr)]">
              <div className="space-y-3">
                <SectionTitle>{T.matches.yourPosition}</SectionTitle>
                <div className={`${card} space-y-3`}>
                  <PositionBody
                    eventId={eventId}
                    signupBase="matches"
                    squadLayout={squads}
                    bench={bench}
                    confirmations={confirmations}
                    userId={user?.id}
                    onConfirmChange={() => { loadRoster(); onRefresh() }}
                  />
                </div>
                {canRoster && onConfigureRoster && (
                  <Button size="sm" variant="outline" className="h-8 w-full gap-1.5 text-xs" onClick={onConfigureRoster}>
                    <Settings2 className="h-3.5 w-3.5" />{T.matches.editRoster}
                  </Button>
                )}
              </div>
              <div className="min-w-0">
                <SquadDisplaySection
                  squads={squads}
                  confirmations={confirmations}
                  signUps={signUps}
                  layoutLabel={T.eventDetail.rosterLayout}
                />
              </div>
            </div>
          ) : (
            // Nothing to confirm and nothing to show: one message, not two.
            <div className="flex min-h-48 flex-col items-center justify-center gap-2 rounded-sm border border-dashed border-white/10 p-6 text-center">
              <p className="text-sm text-foreground/80">{T.matches.stepPositionWaiting}</p>
              <p className="max-w-sm text-xs text-muted-foreground">
                {canRoster ? T.matches.rosterWaitingStaff : T.matches.rosterWaitingHint}
              </p>
              {canRoster && onConfigureRoster && (
                <Button size="sm" variant="outline" className="mt-2 h-8 gap-1.5 text-xs" onClick={onConfigureRoster}>
                  <Settings2 className="h-3.5 w-3.5" />{T.matches.buildRoster}
                </Button>
              )}
            </div>
          ))}

          {step === 'strategy' && (
            <div className="max-w-xl">
              <SectionTitle>{T.matches.strategyTitle}</SectionTitle>
              <div className={card}>
                <MatchStrategyCard eventId={eventId} canPublish={canEdit} bare onChange={onRefresh} />
              </div>
              {!canEdit && !progress?.strategy && (
                <p className="mt-3 text-xs text-muted-foreground">{T.matches.strategyWaitingHint}</p>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
